import React, { useState } from 'react';
import { Tabs } from "@shared/ui/tabs";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ArrowRight } from "lucide-react";
import { useParams } from 'react-router-dom';
import { useUtilityInfo, useGenerateOtp } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { useToast } from "@shared/hooks/use-toast";

export const VerifyIdentity = ({ onNext }: any) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'email' | 'acct'>('email');

  const emailFields: FormField[] = [
    { name: 'email', label: t("Email"), type: 'email', required: true, placeholder: t("e.g. michael.johnson@email.com"), defaultValue: '' }
  ];

  const consumerFields: FormField[] = [
    { name: 'consumerNumber', label: t("Account Number"), type: 'text', required: true, placeholder: t("e.g. 123456789"), defaultValue: '' }
  ];

  const emailForm = FormService.createForm({ fields: emailFields, mode: "onChange", validateOnMount: false });
  const consumerForm = FormService.createForm({ fields: consumerFields, mode: "onChange", validateOnMount: false });
  const { tenant, utilityName } = useParams<{ tenant?: string; utilityName?: string }>();
  
  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenant || '', utility_alias: utilityName || '' },
    { enabled: !!tenant && !!utilityName }
  );

  const generateOtpMutation = useGenerateOtp();
  
  const onSubmit = (data: any) => {
    const isEmail = activeTab === 'email' || !!data.email;
    const identifierValue = isEmail ? data.email : data.consumerNumber;
    const remoteUtilityId = utilityInfo?.result?.id || Number(localStorage.getItem('remoteUtilityId')) || 0;

    const payload: any = {
      remote_utility_id: remoteUtilityId
    };

    if (isEmail) {
      payload.email = identifierValue;
    } else {
      payload.consumer_no = identifierValue;
    }

    generateOtpMutation.mutate(payload, {
      onSuccess: (res) => {
        console.log("OTP Sent", res);
        sessionStorage.setItem('forgot_password_identity_type', isEmail ? 'email' : 'consumer_no');
        sessionStorage.setItem('forgot_password_identity_value', identifierValue);
        sessionStorage.setItem('forgot_password_email', identifierValue);
        
        // Robustly extract secret_key from various possible response shapes
        const secretKey = 
          res?.otp?.result?.secretKey || 
          res?.result?.otp?.result?.secretKey || 
          res?.data?.otp?.result?.secretKey || 
          res?.secretKey;
          
        if (secretKey) {
          sessionStorage.setItem('forgot_password_secret_key', secretKey);
        } else {
          console.error("Could not find secret_key in response:", res);
        }
        
        onNext();
      },
      onError: (err: any) => {
        console.error("Failed to send OTP", err);
        const errorMessage = err?.response?.data?.message || err?.message || "Failed to send OTP.";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    });
  };

  const handleFieldChange = (formInstance: any, fieldName: string) => {
    if (FormService.getFieldError(formInstance, fieldName)) {
      formInstance.clearErrors(fieldName);
    }
  };

  const handleReset = (formInstance: any) => {
    formInstance.reset();
  };

  const renderFooter = () => (
    <Button type="submit" variant="default" className="w-full mt-4 bg-blue-600 hover:bg-blue-700 h-12 text-base">
      {t("Send Verification Code")} <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  );

  const tabComponents = {
    email: {
      label: t("Email"),
      component: (
        <DynamicForm 
          fields={emailFields} 
          form={emailForm} 
          onSubmit={onSubmit}
          onReset={() => handleReset(emailForm)}
          onFieldChange={(fieldName) => handleFieldChange(emailForm, fieldName)}
          config={{ classes: FIELD_CLASSES }}
          renderCustomFooter={renderFooter}
        />
      )
    },
    acct: {
      label: t("Acct No."),
      component: (
        <DynamicForm 
          fields={consumerFields} 
          form={consumerForm} 
          onSubmit={onSubmit}
          onReset={() => handleReset(consumerForm)}
          onFieldChange={(fieldName) => handleFieldChange(consumerForm, fieldName)}
          config={{ classes: FIELD_CLASSES }}
          renderCustomFooter={renderFooter}
        />
      )
    }
  };

  return (
    <div className="flex flex-col p-2">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("Forgot your password?")}</h2>
      <p className="text-gray-500 text-sm mb-6">{t("Enter your account identifier and we'll send a verification code.")}</p>
      
      <Tabs 
        defaultValue="email"
        onValueChange={(val) => {
          setActiveTab(val as 'email' | 'acct');
          emailForm.reset();
          consumerForm.reset();
        }}
        tabComponents={tabComponents}
        tabsListClassName="grid grid-cols-2 w-full bg-gray-50 p-1 rounded-lg h-12 mb-6"
        idPrefix="forgot-tab"
        className="w-full"
      />
    </div>
  );
};
